import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import {
	type ActionFunctionArgs,
	data,
	Form,
	type HeadersFunction,
	type LoaderFunctionArgs,
	useActionData,
	useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/errors/error-boundary.tsx'
import {
	CheckboxField,
	ErrorList,
	PublicField,
} from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { BASE_URL } from '#app/utils/config.ts'
import { prisma } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import {
	generateMetaTranslationLoader,
	i18next,
} from '#app/utils/i18next.server.ts'
import { logger } from '#app/utils/logger.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import routes from '#routes/index.js'
import { handleNewSession } from './login.server.ts'

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return {
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'auth',
			keys: {
				title: 'login.title',
				description: 'login.description',
				canonical: `${BASE_URL}${routes.public.auth.login.route({})}`,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

// Prevent Akamai/CDN from caching login responses
export const headers: HeadersFunction = () => {
	return {
		'Cache-Control': 'no-store, no-cache, must-revalidate, private',
		'Pragma': 'no-cache',
		'Expires': '0',
		// Akamai-specific headers
		'Edge-Control': 'no-store',
	}
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)

	const formData = await request.formData()
	const t = await i18next.getFixedT(request, 'auth')

	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				// Log login attempt
				void logger.info('Login attempt', {
					metadata: {
						username: data.username,
						remember: data.remember ?? false,
					},
				})

				const session = await login(data)
				if (!session) {
					// Log failed login
					void logger.warn('Login failed - invalid credentials', {
						metadata: {
							username: data.username,
						},
					})

					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: t('validation.invalidUsernameOrPassword'),
					})
					return z.NEVER
				}

				// Log successful login
				void logger.info('Login successful', {
					userId: session.userId,
					metadata: {
						username: data.username,
						remember: data.remember,
					},
				})

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	let { session, remember, redirectTo } = submission.value

	const user = await prisma.user.findUnique({
		where: {
			id: session.userId,
		},
		select: {
			roles: true,
		},
	})

	if (user!.roles.some((role) => role.name === 'admin')) {
		redirectTo = redirectTo ?? '/admin'
	}

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const { t } = useTranslation('auth')
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex min-h-full flex-col justify-center">
			<div className="mx-auto w-full max-w-md">
				<div className="flex flex-col items-center justify-center gap-sm">
					<Text as={'h1'} size={'h2'} type={'serif'} weight={'light'}>
						{t('login.title')}
					</Text>
					<Text>{t('login.subtitle')}</Text>
				</div>
				<Spacer size="xs" />

				<div>
					<div className="mx-auto w-full max-w-md px-8">
						<Form
							method="POST"
							{...getFormProps(form)}
							className="flex flex-col gap-sm"
						>
							<HoneypotInputs />
							<PublicField
								placeholder={t('fields.username')}
								inputProps={{
									...getInputProps(fields.username, { type: 'text' }),
									autoFocus: true,
									className: 'lowercase',
									autoComplete: 'username',
								}}
								errors={fields.username.errors}
							/>

							<PublicField
								placeholder={t('fields.password')}
								inputProps={{
									...getInputProps(fields.password, {
										type: 'password',
									}),
									autoComplete: 'current-password',
								}}
								errors={fields.password.errors}
							/>

							<div className="flex justify-between">
								<CheckboxField
									labelProps={{
										htmlFor: fields.remember.id,
										children: t('fields.rememberMe'),
									}}
									buttonProps={{
										...getInputProps(fields.remember, {
											type: 'checkbox',
										}),
										className: 'rounded-full',
									}}
									errors={fields.remember.errors}
								/>
								<LocalizedLink to={routes.public.auth.forgotPassword.route({})}>
									<Text size={'small'}>{t('forgotPassword.title')}</Text>
								</LocalizedLink>
							</div>

							<input
								{...getInputProps(fields.redirectTo, { type: 'hidden' })}
							/>
							<ErrorList errors={form.errors} id={form.errorId} />

							<div className="flex items-center justify-between gap-6 pt-3">
								<StatusButton
									variant={'outline'}
									className="w-full"
									status={isPending ? 'pending' : (form.status ?? 'idle')}
									type="submit"
									disabled={isPending}
								>
									{t('login.title')}
								</StatusButton>
							</div>
						</Form>
						{/*<div className="flex items-center justify-center gap-2 pt-6">*/}
						{/*	<span className="text-muted-foreground">New here?</span>*/}
						{/*	<Link*/}
						{/*		to={*/}
						{/*			redirectTo*/}
						{/*				? `${routes.public.auth.signup.route({})}?${encodeURIComponent(redirectTo)}`*/}
						{/*				: routes.public.auth.signup.route({})*/}
						{/*		}*/}
						{/*	>*/}
						{/*		Create an account*/}
						{/*	</Link>*/}
						{/*</div>*/}
					</div>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
